<script setup lang="ts">
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useAppLoading,
    useModalStack
  } from '@gat-ship/vue-components'
  import { IconTrash } from '@gat-ship/icons'
  import type { AccessRole } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import { ref } from 'vue'
  import { useAccessControlApiCalls } from '../../composables/useAccessControlApiCalls'
  import {
    useAccessRoleDetailProvider,
    type ShowDeleteAccessRoleDialogParams
  } from '../providers/access-role-detail-context'
  import { useAccessRolesCollectionProvider } from '../providers/access-roles-collection-context'

  const accessRolesProvider = useAccessRolesCollectionProvider()
  const stack = useModalStack()
  const { dataSource } = useAccessRoleDetailProvider()
  const { deleteAccessRoleRequest } = useAccessControlApiCalls()
  const { setLoadingId, removeLoadingId } = useAppLoading()

  const isOpen = ref(false)
  const accessRole = ref<AccessRole>()

  function showModal(params: ShowDeleteAccessRoleDialogParams) {
    accessRole.value = params.accessRole
    isOpen.value = true
  }

  async function onDeleteAccessRole(): Promise<void> {
    const role = accessRole.value
    if (!role) return
    isOpen.value = false
    setLoadingId(['delete-access-role-btn', role.id])
    try {
      await deleteAccessRoleRequest({
        roleId: role.id,
        commandId: globalUtils.functions.uuid()
      })
      accessRolesProvider.dataSource.removeAccessRole(role.id)
      // Close the access-role detail modal after deletion (was a route nav back
      // to the access-roles list before central-modal-host); no-op if the
      // delete was triggered from the admin panel list with no detail open.
      stack.close('access-role')
      dataSource.clearData({ unsubscribeFromEvents: true })
    } finally {
      removeLoadingId(['delete-access-role-btn', role.id])
    }
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconTrash class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Delete Role
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <div class="whitespace-pre-line text-sm">
          Are you sure you want to delete {{ accessRole?.name ?? '' }}?
        </div>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="destructive" @click="onDeleteAccessRole()">
        Delete Role
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
